/**
 * HostA Global - Property Rental Management System
 * Sign In Page
 */

import { useState } from "react";

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

// Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Auth context & API
import { useAuth } from "context/authContext";
import { authAPI } from "utils/api";

// Images
import logo from "assets/images/logo-ct.png";

const isEmailNotVerifiedError = (message) => {
  if (!message || typeof message !== "string") return false;
  const lower = message.toLowerCase();
  return lower.includes("verify your email") || lower.includes("verification");
};

function Basic() {
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState("");
  const { login, userRole } = useAuth();
  const navigate = useNavigate();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const showResendVerification = error && isEmailNotVerifiedError(error);

  const handleResendVerification = async () => {
    if (!email || resendLoading) return;
    setResendError("");
    setResendSuccess(false);
    setResendLoading(true);
    try {
      await authAPI.resendVerificationEmail(email.trim());
      setResendSuccess(true);
      setResendError("");
    } catch (err) {
      setResendError(err.message || "Failed to resend. Please try again later.");
      setResendSuccess(false);
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResendSuccess(false);
    setResendError("");

    try {
      const result = await login(email, password);

      if (result.success) {
        // Redirect based on role (backend returns role as string)
        const role = result.user?.role || userRole;
        if (role === "Super_Admin") {
          navigate("/super-admin/dashboard", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
      } else {
        setError(result.error || "Login failed. Please check your credentials.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  return (
    <BasicLayout showFooter={false}>
      <Card
        sx={{
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          borderRadius: 3,
        }}
      >
        <MDBox
          borderRadius="lg"
          mx={2}
          mt={2}
          p={2}
          mb={{ xs: 2, sm: 2.5, md: 3 }}
          textAlign="center"
          sx={{
            background: "#b6925b",
            boxShadow: "none",
          }}
        >
          <MDBox
            component="img"
            src={logo}
            alt="HostA Global"
            sx={{
              width: "100%",
              maxWidth: 220,
              height: "auto",
              objectFit: "contain",
              borderRadius: "5px",
              mt: 1,
              mb: 1,
              // Remove white background from logo (blend with dark)
              mixBlendMode: "multiply",
              filter: "contrast(1.05)",
            }}
          />
          <MDTypography variant="body2" sx={{ color: "#e8e8e8" }} mt={0.5} mb={1}>
            Property Rental Management System
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.85)" }} mt={0.5} mb={1}>
            Administration Dashboard
          </MDTypography>
          <MDTypography variant="h6" fontWeight="regular" sx={{ color: "#fff" }} mt={2}>
            Sign in to your account
          </MDTypography>
        </MDBox>
        <MDBox
          pt={{ xs: 3, sm: 4, md: 4.5 }}
          pb={{ xs: 3, sm: 3.5, md: 4 }}
          px={{ xs: 2.5, sm: 3, md: 3.5 }}
        >
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {error && (
              <MDBox mb={2}>
                {showResendVerification ? (
                  <Alert
                    severity="warning"
                    sx={{
                      alignItems: "center",
                      "& .MuiAlert-message": { width: "100%" },
                    }}
                  >
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      color="warning"
                      display="block"
                      mb={0.5}
                    >
                      Email not verified
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                      {error}
                    </MDTypography>
                    {resendSuccess && (
                      <MDTypography variant="caption" color="success" display="block" mb={1}>
                        Verification email sent. Please check your inbox and spam folder.
                      </MDTypography>
                    )}
                    {resendError && (
                      <MDTypography variant="caption" color="error" display="block" mb={1}>
                        {resendError}
                      </MDTypography>
                    )}
                    <MDButton
                      type="button"
                      variant="outlined"
                      size="small"
                      disabled={resendLoading}
                      onClick={handleResendVerification}
                      sx={{
                        borderColor: "#b6925b",
                        color: "#b6925b",
                        "&:hover": {
                          borderColor: "#a67d4d",
                          backgroundColor: "rgba(182, 146, 91, 0.08)",
                        },
                      }}
                    >
                      {resendLoading ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 0.5 }} color="inherit" />
                          Sending...
                        </>
                      ) : (
                        "Resend verification email"
                      )}
                    </MDButton>
                  </Alert>
                ) : (
                  <MDTypography variant="caption" color="error">
                    {error}
                  </MDTypography>
                )}
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </MDBox>
            <MDBox mb={1} textAlign="right">
              <MDTypography
                component={Link}
                to="/authentication/forgot-password"
                variant="button"
                fontWeight="medium"
                sx={{
                  color: "#b6925b",
                  fontSize: "0.875rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Forgot password?
              </MDTypography>
            </MDBox>
            {/* <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Remember me
              </MDTypography>
            </MDBox> */}
            <MDBox mt={4} mb={1}>
              <MDButton
                fullWidth
                type="submit"
                disabled={loading}
                sx={{
                  background: "#b99160",
                  color: "#fff",
                  "&:hover": { background: "#a67d4d" },
                  "&:disabled": { background: "#8a7358", color: "rgba(255,255,255,0.7)" },
                }}
              >
                {loading ? "Signing in..." : "sign in"}
              </MDButton>
            </MDBox>
            <MDBox mt={3} mb={1} textAlign="center">
              {/* <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign up
                </MDTypography>
              </MDTypography> */}
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;
